import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dropdown, Badge, Spin, Tag, Button } from 'antd';
import {
    BellOutlined,
    CheckOutlined,
    InboxOutlined,
    RightOutlined,
    BugOutlined,
    ProjectOutlined,
    MessageOutlined,
    CheckCircleOutlined,
    CalendarOutlined,
    RocketOutlined,
    NotificationOutlined,
} from '@ant-design/icons';
import { useNotifications } from '@hooks/useNotifications';

const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    if (diffMs < 604800000) return `${Math.floor(diffMs / 86400000)}d ago`;
    return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    });
};

const getNotificationIconMeta = (type) => {
    if (!type) {
        return {
            icon: <BellOutlined style={{ color: '#7c3aed', fontSize: 14 }} />,
            bg: '#f3e8ff',
        };
    }
    const t = type.toLowerCase();
    if (t.includes('leave')) {
        return {
            icon: <CalendarOutlined style={{ color: '#d97706', fontSize: 14 }} />,
            bg: '#fef3c7',
        };
    }
    if (t.includes('bug') || t.includes('report')) {
        return {
            icon: <BugOutlined style={{ color: '#dc2626', fontSize: 14 }} />,
            bg: '#fee2e2',
        };
    }
    if (t.includes('milestone') || t.includes('sprint') || t.includes('rocket')) {
        return {
            icon: <RocketOutlined style={{ color: '#2563eb', fontSize: 14 }} />,
            bg: '#dbeafe',
        };
    }
    if (t.includes('project')) {
        return {
            icon: <ProjectOutlined style={{ color: '#0284c7', fontSize: 14 }} />,
            bg: '#e0f2fe',
        };
    }
    if (t.includes('chat') || t.includes('message')) {
        return {
            icon: <MessageOutlined style={{ color: '#059669', fontSize: 14 }} />,
            bg: '#d1fae5',
        };
    }
    if (t.includes('done') || t.includes('complete') || t.includes('pass')) {
        return {
            icon: <CheckCircleOutlined style={{ color: '#16a34a', fontSize: 14 }} />,
            bg: '#dcfce7',
        };
    }
    return {
        icon: <NotificationOutlined style={{ color: '#7c3aed', fontSize: 14 }} />,
        bg: '#f3e8ff',
    };
};

export const NotificationHeaderDropdown = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const { items, isLoading, markRead, markAllRead, markAllReadPending, unreadCount } = useNotifications({
        limit: 8,
        page: 1,
    });

    const isTenant = location.pathname.startsWith('/tenant');

    const handleNotificationClick = (n) => {
        if (!n.read) {
            markRead(n.id);
        }
        setDropdownOpen(false);

        const meta = n.metadata ?? {};
        const projectId = meta.projectId ?? meta.project_id;
        const chatId = meta.chatId ?? meta.chat_id;
        const leadId = meta.leadId ?? meta.lead_id;

        if (projectId) {
            navigate(isTenant ? `/tenant/projects/${projectId}` : `/projects/${projectId}`);
        } else if (chatId) {
            navigate(isTenant ? `/tenant/chat/${chatId}` : `/chat/${chatId}`);
        } else if (leadId) {
            navigate(isTenant ? `/tenant/sales/leads` : `/sales/leads`);
        } else if (n.type?.includes('bug') || n.type?.includes('report')) {
            navigate(isTenant ? `/tenant/bug-reports` : `/bug-reports`);
        } else {
            navigate(isTenant ? `/tenant/notifications` : `/notifications`);
        }
    };

    const handleViewAll = () => {
        setDropdownOpen(false);
        navigate(isTenant ? `/tenant/notifications` : `/notifications`);
    };

    const dropdownMenuContent = (
        <div
            style={{
                width: 380,
                backgroundColor: '#ffffff',
                borderRadius: 14,
                boxShadow: '0 12px 36px -4px rgba(15, 23, 42, 0.15), 0 4px 12px -2px rgba(15, 23, 42, 0.08)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#ffffff',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', letterSpacing: '-0.2px' }}>
                        Notifications
                    </span>
                    {unreadCount > 0 && (
                        <span
                            style={{
                                background: '#f3e8ff',
                                color: '#7c3aed',
                                fontSize: 11,
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            {unreadCount} unread
                        </span>
                    )}
                </div>

                {unreadCount > 0 && (
                    <Button
                        type="text"
                        size="small"
                        loading={markAllReadPending}
                        onClick={() => markAllRead()}
                        style={{
                            fontSize: 11.5,
                            color: '#7c3aed',
                            fontWeight: 600,
                            padding: '2px 8px',
                            height: 'auto',
                            borderRadius: 6,
                        }}
                    >
                        Mark all read
                    </Button>
                )}
            </div>

            {/* Notification List */}
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                {isLoading && items.length === 0 ? (
                    <div style={{ padding: 28, textAlign: 'center' }}>
                        <Spin size="small" />
                    </div>
                ) : items.length === 0 ? (
                    <div style={{ padding: '36px 16px', textAlign: 'center', color: '#94a3b8' }}>
                        <InboxOutlined style={{ fontSize: 32, color: '#cbd5e1', marginBottom: 8 }} />
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>All caught up!</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>No notifications right now</div>
                    </div>
                ) : (
                    items.map((item) => {
                        const title = item.title ?? item.message ?? 'Notification';
                        const body = item.body ?? item.message ?? item.description ?? '';
                        const showBody = body && body.trim() !== title.trim();
                        const { icon, bg } = getNotificationIconMeta(item.type);

                        return (
                            <div
                                key={item.id}
                                onClick={() => handleNotificationClick(item)}
                                style={{
                                    padding: '11px 16px',
                                    borderBottom: '1px solid #f8fafc',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 12,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    background: item.read ? '#ffffff' : '#faf5ff',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = item.read ? '#f8fafc' : '#f3e8ff')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = item.read ? '#ffffff' : '#faf5ff')}
                            >
                                {/* Category Icon Badge */}
                                <div
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 9,
                                        background: bg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginTop: 1,
                                    }}
                                >
                                    {icon}
                                </div>

                                {/* Main Text Block */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                                        <span
                                            style={{
                                                fontWeight: item.read ? 600 : 700,
                                                fontSize: 12.5,
                                                color: '#0f172a',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 210,
                                            }}
                                            title={title}
                                        >
                                            {title}
                                        </span>
                                        <span style={{ fontSize: 10.5, color: '#94a3b8', flexShrink: 0, fontWeight: 500 }}>
                                            {formatRelativeTime(item.createdAt ?? item.created_at)}
                                        </span>
                                    </div>

                                    {/* Detail / Body text with clean 2-line max ellipsis */}
                                    {showBody && (
                                        <div
                                            style={{
                                                fontSize: 11.5,
                                                color: '#64748b',
                                                marginTop: 2,
                                                lineHeight: 1.35,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: 270,
                                            }}
                                            title={body}
                                        >
                                            {body}
                                        </div>
                                    )}
                                </div>

                                {/* Unread indicator dot */}
                                {!item.read && (
                                    <div
                                        style={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: '50%',
                                            background: '#7c3aed',
                                            flexShrink: 0,
                                            marginTop: 6,
                                            boxShadow: '0 0 0 2px #f3e8ff',
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer */}
            <div
                style={{
                    padding: '10px 16px',
                    borderTop: '1px solid #f1f5f9',
                    textAlign: 'center',
                    background: '#f8fafc',
                }}
            >
                <Button
                    type="text"
                    block
                    onClick={handleViewAll}
                    style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: '#7c3aed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        height: 32,
                        borderRadius: 8,
                    }}
                >
                    <span>Show More</span>
                    <RightOutlined style={{ fontSize: 11 }} />
                </Button>
            </div>
        </div>
    );

    return (
        <Dropdown
            dropdownRender={() => dropdownMenuContent}
            trigger={['click']}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            placement="bottomRight"
        >
            <button
                type="button"
                aria-label="Notifications"
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: dropdownOpen ? '#7c3aed' : '#64748b',
                    transition: 'all 0.15s ease',
                    padding: 0,
                    outline: 'none',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#cbd5e1';
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                }}
            >
                <Badge count={unreadCount} overflowCount={99} size="small" offset={[2, -2]}>
                    <BellOutlined style={{ fontSize: 15 }} />
                </Badge>
            </button>
        </Dropdown>
    );
};

export default NotificationHeaderDropdown;
